import { Link } from 'react-router-dom';
import { Eyebrow } from '@/components/ui/Typography';

// Mesmo gradiente do `VacuumCta` da tela de recarga (ActiveCharge) — os dois
// toques do aspirador precisam ler como o MESMO produto.
const VACUUM_RAIL = 'linear-gradient(180deg, #00C072, #00BBDE)';

interface Props {
  /** Estação da recarga recém-concluída — destino da oferta. */
  stationId: string;
  /** Bloco mais barato, vindo do backend (`vacuum_from_brl`). */
  fromBrl: number;
  /** Duração desse bloco, em segundos (`vacuum_from_seconds`). */
  fromSeconds: number;
  /** Cliente ainda tem a 1ª aspiração grátis — vira a manchete do card. */
  courtesy?: boolean;
}

/**
 * Convite ao aspirador no pós-recarga (Receipt).
 *
 * Ocupa o mesmo slot inline do `AliancaReceiptCard` e tem PRECEDÊNCIA sobre
 * ele: o convite do aspirador é perecível — só vale enquanto o cliente ainda
 * está fisicamente no pátio —, enquanto o da Aliança é durável e reaparece em
 * todo recibo futuro (e tem aba própria). O gate fica no caller.
 *
 * O card de NPS (`PostChargeRating`) é overlay fixo separado, fora da árvore
 * do miolo — este card não o afeta.
 */
export function VacuumReceiptCard({
  stationId,
  fromBrl,
  fromSeconds,
  courtesy = false
}: Props) {
  const preco = fromBrl.toFixed(2).replace('.', ',');
  const minutos = Math.round(fromSeconds / 60);
  return (
    <Link
      to={`/amenities/${stationId}/vacuum?origin=receipt`}
      className="relative flex items-center gap-3.5 overflow-hidden rounded-[22px] bg-white px-4 py-3.5"
      style={{ boxShadow: '0 1px 2px rgba(0,27,116,0.04), 0 6px 16px rgba(0,27,116,0.06)' }}
    >
      <span aria-hidden className="absolute inset-y-0 left-0 w-[5px]" style={{ background: VACUUM_RAIL }} />
      <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-full bg-bow-cream">
        <img src="/logo/aspirador-bow.png" alt="" className="h-7 w-7 object-contain" />
      </span>
      <div className="min-w-0 flex-1">
        <Eyebrow>{courtesy ? 'Sua cortesia' : 'Aspiração'}</Eyebrow>
        <div
          className="mt-0.5 leading-[1.22] text-bow-royal"
          style={{
            fontFamily: 'Chillax, system-ui, sans-serif',
            fontWeight: 500,
            fontSize: 16,
            letterSpacing: '-0.02em'
          }}
        >
          {courtesy ? (
            <>
              Sua primeira aspiração é{' '}
              <span style={{ color: '#00A664' }}>grátis — {minutos} min</span>
            </>
          ) : (
            <>
              Aproveite que está aqui —{' '}
              <span style={{ color: '#00A664' }}>
                R$ {preco} / {minutos} min
              </span>
            </>
          )}
        </div>
      </div>
      <span className="inline-flex h-[38px] shrink-0 items-center gap-1.5 rounded-full bg-bow-royal px-4 text-[13.5px] font-semibold text-bow-cream">
        {courtesy ? 'Usar' : 'Aspirar'}
        <ArrowIcon />
      </span>
    </Link>
  );
}

function ArrowIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  );
}
